import {
  useCallback,
  useContext,
  useMemo,
  useState,
  type MouseEvent,
} from 'react';
import { mapPrivacyToVisibilityInt } from '@wlb/shared/api/services/feedService';
import { buildSharePostUrl } from '@wlb/shared/config/env';
import { useFeed } from '@wlb/shared/context/FeedContext';
import groupContext from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { FeedPost } from '@wlb/shared/types/post';
import { useAuthGate } from '../../components/feedback/AuthGate';
import {
  buildReshareLineageUrl,
  extractReshareLineage,
  extractReshareSourcePostId,
  stripReshareMetadataFromText,
} from '../../utils/reshareAttribution';
import { buildInAppShareBody, canQuickReshare } from './sharePostRequest';

export type PostReshareChoice = 'reshare' | 'withThoughts';

export const usePostReshare = (
  post: FeedPost,
  onWithThoughts: () => void,
  onReshared?: () => void,
) => {
  const { t } = useTranslation();
  const { requireAuth } = useAuthGate();
  const { sharePost, requestFeedRefresh } = useFeed();
  const group = useContext(groupContext);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [resharing, setResharing] = useState(false);
  const lineage = useMemo(() => extractReshareLineage(post.text), [post.text]);

  const openMenu = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const nextAnchor = event.currentTarget.getBoundingClientRect();
      requireAuth(() => setAnchorRect(nextAnchor));
    },
    [requireAuth],
  );

  const closeMenu = useCallback(() => setAnchorRect(null), []);

  const reshareInstantly = useCallback(async () => {
    if (resharing) return;
    setAnchorRect(null);
    setResharing(true);

    try {
      const rootPostId = extractReshareSourcePostId(lineage) || post.id;
      const originalAuthor = lineage?.originalAuthor ?? {
        userGuid: post.profileUserGuid,
        username: post.username,
      };
      const originalText =
        lineage?.originalText || stripReshareMetadataFromText(post.text);
      const sourceUrl = buildReshareLineageUrl(
        lineage?.sourceUrl || buildSharePostUrl(rootPostId),
        originalAuthor,
        [],
      );
      const bodyText = buildInAppShareBody({
        message: '',
        originalText,
        shareUrl: sourceUrl,
        attribution: t('bottomSheet.originalPostBy', {
          username: `@${originalAuthor.username}`,
        }),
      });
      const result = await sharePost(
        rootPostId,
        bodyText,
        post.groupId,
        mapPrivacyToVisibilityInt(post.privacy ?? post.postVisibility),
      );
      if (!result.success) throw new Error('Reshare failed');

      if (post.groupId) group?.updateGroupShareCount(post.id);
      requestFeedRefresh();
      onReshared?.();
      ToastService.show({
        type: 'success',
        message: t('bottomSheet.inAppShareSuccess'),
      });
    } catch {
      ToastService.show({
        type: 'error',
        message: t('bottomSheet.inAppShareFailed'),
      });
    } finally {
      setResharing(false);
    }
  }, [
    group,
    lineage,
    post.groupId,
    post.id,
    post.postVisibility,
    post.privacy,
    post.profileUserGuid,
    post.text,
    post.username,
    requestFeedRefresh,
    resharing,
    sharePost,
    t,
    onReshared,
  ]);

  const select = useCallback(
    (choice: PostReshareChoice) => {
      if (choice === 'withThoughts') {
        setAnchorRect(null);
        onWithThoughts();
        return;
      }
      reshareInstantly().catch(() => undefined);
    },
    [onWithThoughts, reshareInstantly],
  );

  return {
    anchorRect,
    canReshare: canQuickReshare(post),
    resharing,
    openMenu,
    closeMenu,
    select,
  };
};
